// src/hooks/useLojaMutations.ts
import { useRef, useState } from 'react';
import axios from 'axios';
import { apiPost, apiPut } from '../services/api';
import { Loja } from '../types/parametrizacao/Loja';
import { useDragAndDrop } from './useDragAndDrop';
import { showAlert } from '../contexts/DialogContext';
import { Toast } from '../contexts/ToastContext';
import { logger } from '@/utils/logger';

type SourceColumn = 'semCadastro' | 'cadastrada' | 'inativas';

interface Options {
  onReload: () => void;
}

export const useLojaMutations = ({ onReload }: Options) => {
  const [selectedLoja, setSelectedLoja] = useState<Loja | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // Guard síncrono contra double-drop: impede um 2º toggle enquanto o PUT do
  // primeiro ainda está em voo (ref muda na hora, sem esperar re-render).
  const togglingRef = useRef(false);

  // Toggle do ciclo de vida da loja via PUT { ativo }. Usado pelos drags
  // cadastrada→inativas (inativar) e inativas→cadastrada (reativar). Sem side panel.
  const handleToggleAtivo = async (loja: Loja, ativo: boolean) => {
    if (!loja.id || togglingRef.current) return;
    togglingRef.current = true;
    setIsSaving(true);
    try {
      await apiPut(`/v1/lojas-acesso/${loja.id}`, {
        codFilial: loja.codFilial,
        cnpj: loja.cnpj,
        nomeFantasia: loja.nomeFantasia,
        razaoSocial: loja.razaoSocial,
        ativo,
      });
      Toast.success(ativo ? 'Loja reativada com sucesso!' : 'Loja inativada com sucesso!');
      onReload();
    } catch (err) {
      // Reativar pode bater no LIMITE_FILIAIS → 409 com `error` pronto. Lê `.error`
      // (contrato do 409) com fallback p/ `.message` (padrão do resto do codebase).
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const data = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string; message?: string })
        : undefined;
      const apiMsg = data?.error ?? data?.message;
      const fallback = ativo ? 'Erro ao reativar loja' : 'Erro ao inativar loja';
      await showAlert(apiMsg ?? fallback, { variant: status === 409 ? 'warning' : 'error' });
    } finally {
      setIsSaving(false);
      togglingRef.current = false;
    }
  };

  const drag = useDragAndDrop<Loja, SourceColumn>({
    onDropHandler: async (data, source, target) => {
      if (source === target) return;
      // Integrar nova loja: abre o side panel (POST no save). Inalterado.
      if (source === 'semCadastro' && target === 'cadastrada') {
        setSelectedLoja({
          codFilial: data.codFilial,
          cnpj: data.cnpj,
          nomeFantasia: data.nomeFantasia,
          razaoSocial: data.razaoSocial,
        });
        setShowForm(true);
        return;
      }
      // Inativar: arrasta de Cadastrada → Inativas. PUT direto, sem side panel.
      if (source === 'cadastrada' && target === 'inativas') {
        await handleToggleAtivo(data, false);
        return;
      }
      // Reativar: arrasta de Inativas → Cadastrada. PUT direto (pode 409).
      if (source === 'inativas' && target === 'cadastrada') {
        await handleToggleAtivo(data, true);
        return;
      }
      // Demais combinações: sem efeito.
    },
  });

  const onDragStartSemCadastro = (data: Loja) => drag.onDragStart(data, 'semCadastro');
  const onDragStartCadastrada = (data: Loja) => drag.onDragStart(data, 'cadastrada');
  const onDragStartInativas = (data: Loja) => drag.onDragStart(data, 'inativas');

  const handleFormChange = (field: keyof Loja, value: string) => {
    if (!selectedLoja) return;
    setSelectedLoja({ ...selectedLoja, [field]: value });
  };

  const handleSave = async () => {
    if (!selectedLoja) return;

    setIsSaving(true);
    const bodyToSend = {
      codFilial: selectedLoja.codFilial,
      cnpj: selectedLoja.cnpj,
      nomeFantasia: selectedLoja.nomeFantasia,
      razaoSocial: selectedLoja.razaoSocial,
    };

    try {
      if (!selectedLoja.id) {
        await apiPost('/v1/lojas-acesso', bodyToSend);
        Toast.success('Loja cadastrada com sucesso!');
      } else {
        await apiPut(`/v1/lojas-acesso/${selectedLoja.id}`, bodyToSend);
        Toast.success('Loja atualizada com sucesso!');
      }
      setShowForm(false);
      setSelectedLoja(null);
      onReload();
    } catch (err) {
      logger.error('Erro ao salvar:', err);
      // Contrato de erro desta rota sempre traz `error` (409 limite_atingido:LIMITE_FILIAIS
      // ao cadastrar; 400 CNPJ/filial duplicada). Lê `.error` primeiro, com `.message`
      // de fallback. 409 → warning (mesmo padrão de handleToggleAtivo).
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const data = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string; message?: string })
        : undefined;
      const errorMessage = data?.error ?? data?.message ?? 'Erro ao salvar loja';
      await showAlert(errorMessage, { variant: status === 409 ? 'warning' : 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedLoja(null);
  };

  const handleEditClick = (loja: Loja) => {
    setSelectedLoja(loja);
    setShowForm(true);
  };

  return {
    selectedLoja,
    isSaving,
    showForm,
    onDragStartSemCadastro,
    onDragStartCadastrada,
    onDragStartInativas,
    onDragOver: drag.onDragOver,
    onDrop: drag.onDrop,
    handleFormChange,
    handleSave,
    handleToggleAtivo,
    handleCancelForm,
    handleEditClick,
  };
};
